// What the surfaces share: the frame, the error overlay and the "Tap to listen" rule.
import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { TapToListenButton } from "../TapToListen";
import { useBlocked } from "../useBlocked";
import type { MediaEvent, MediaFrame } from "../media";
import type { SurfaceProps } from "../PlayableMedia";

export function useLatest<T>(v: T) {
  const r = useRef(v);
  r.current = v;
  return r;
}

/** The screen's frame (or a plain full-size box) holding the media, then the overlays, then the screen's extras. */
export function Frame({ frame, media, overlays }: { frame: MediaFrame | undefined; media: ReactNode; overlays: ReactNode }) {
  const f = frame ?? { className: "relative w-full h-full" };
  return (
    <div ref={f.ref} data-testid={f.testId} className={f.className} style={f.style}>
      {f.wrap ? f.wrap(media) : media}
      {overlays}
      {f.extra}
    </div>
  );
}

export function ErrorOverlay() {
  const { t } = useTranslation();
  return (
    <div data-testid="player-error" className="absolute inset-0 flex items-center justify-center bg-black/70 text-white text-[13px]">
      {t("cant_play_link", "Can't play this link")}
    </div>
  );
}

/** The event sink: always the screen's latest handler. */
export function useEmit(p: SurfaceProps) {
  const onEvent = useLatest(p.onEvent);
  return (e: MediaEvent) => onEvent.current?.(e);
}

/**
 * "Tap to listen": blocked (play() refused, or `armed` and silent too long), drawn over the frame, under it (a kind
 * nothing may cover while its provider controls show: YouTube), or handed out (`tapToListenOut`). The tap clears it
 * and plays, or does what the screen says (`onTapToListen`).
 */
export function useTapToListen(p: SurfaceProps, armed: boolean, play: () => void, overlayable: boolean) {
  const [blocked, setBlocked] = useBlocked(armed, p.blockedKey, p.blockedDelayMs);
  const latest = useLatest({ onTap: p.onTapToListen, play, out: p.tapToListenOut });
  const tap = () => {
    setBlocked(false);
    (latest.current.onTap ?? latest.current.play)();
  };
  const out = p.tapToListenOut != null;
  useEffect(() => {
    const hand = latest.current.out;
    if (!hand) return;
    hand(blocked ? tap : null);
    // The one it was handed to: the screen may stop passing it (left full screen) before this cleanup runs.
    return () => hand(null);
    // tap only touches refs / setters: stable enough for its lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [out, blocked]);
  const under = !overlayable || (p.kind.caps.nothingOver && p.providerControls === true);
  return {
    blocked,
    setBlocked,
    overlay: (failed: boolean) => (blocked && !failed && !out && !under ? <TapToListenButton overlay onTap={tap} /> : null),
    below: (failed: boolean) =>
      blocked && !failed && !out && under ? <TapToListenButton overlay={false} className={p.tapToListenClassName} onTap={tap} /> : null,
  };
}
